import { memo, useEffect, useMemo, useRef, useState } from "react";

import { useCanvasApi } from "../../../context/CanvasContext";
import type { WidgetNodeProps } from "../../../types/canvasNodeData";
import { playChime, unlockChimeOnFirstPointer } from "./chime";
import { TimerFace } from "./TimerFace";
import {
  addMinute,
  deriveView,
  parseDuration,
  parseTimer,
  pause,
  reset,
  setDuration,
  start,
  type TimerData,
  type TimerPhase,
} from "./timerState";

export const TimerWidget = memo(function TimerWidget({
  id,
  data,
}: WidgetNodeProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  const {
    totalSeconds,
    durationSeconds,
    isRunning,
    startedAt,
    elapsedAtPause,
  } = data;
  const timer = useMemo(
    () =>
      parseTimer({
        totalSeconds,
        durationSeconds,
        isRunning,
        startedAt,
        elapsedAtPause,
      }),
    [totalSeconds, durationSeconds, isRunning, startedAt, elapsedAtPause],
  );

  const [tick, setTick] = useState(0);
  const view = deriveView(timer, Date.now());

  // Each update lands on the next second boundary of the shared startedAt,
  // so every client flips on the same frame. Keyed on every field deriveView
  // reads: a remote write (+1 min after zero) must restart a stopped chain.
  useEffect(() => {
    if (view.nextTickMs === null) return;
    const t = setTimeout(() => setTick((n) => n + 1), view.nextTickMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tick,
    timer.totalSeconds,
    timer.durationSeconds,
    timer.isRunning,
    timer.startedAt,
    timer.elapsedAtPause,
  ]);

  useEffect(unlockChimeOnFirstPointer, []);

  // Only a transition this client watched chimes; a load never does.
  const lastPhaseRef = useRef<TimerPhase | null>(null);
  useEffect(() => {
    const last = lastPhaseRef.current;
    lastPhaseRef.current = view.phase;
    if (view.phase === "done" && (last === "running" || last === "urgent"))
      playChime();
  }, [view.phase]);

  // The ref lets Enter or Escape settle the draft before the input's blur.
  const [draft, setDraftState] = useState<string | null>(null);
  const draftRef = useRef<string | null>(null);
  // Clicking away from untouched digits must not turn the time left into
  // the chosen duration.
  const draftSeedRef = useRef<string | null>(null);
  const setDraft = (value: string | null) => {
    draftRef.current = value;
    setDraftState(value);
  };
  const idle = view.phase === "ready" || view.phase === "paused";
  useEffect(() => {
    if (!idle) setDraft(null);
  }, [idle]);

  const write = (next: TimerData) => canvas.updateNodeData(id, { ...next });

  return (
    <TimerFace
      view={view}
      controls={
        canEdit
          ? {
              durationSeconds: timer.durationSeconds,
              draft,
              onStart: () => write(start(timer, Date.now())),
              onPause: () => write(pause(timer, Date.now())),
              onReset: () => write(reset(timer)),
              onAddMinute: () => write(addMinute(timer, Date.now())),
              onChip: (m) => write(setDuration(timer, m * 60)),
              onEditStart: () => {
                draftSeedRef.current = view.digits;
                setDraft(view.digits);
              },
              onDraftChange: setDraft,
              onDraftCommit: () => {
                const text = draftRef.current;
                if (text === null) return;
                if (text.trim() === draftSeedRef.current) {
                  setDraft(null);
                  return;
                }
                const seconds = parseDuration(text);
                if (seconds !== null) write(setDuration(timer, seconds));
                setDraft(null);
              },
              onDraftCancel: () => setDraft(null),
            }
          : undefined
      }
    />
  );
});
